/** Шаг по периодам: «‹ Октябрь ›» вместо календаря.
 *
 *  Прошлый месяц стоил шести действий: «Свой» → календарь «С» → пролистать год
 *  назад → календарь «По» → «Показать». Сравнение с прошлым периодом —
 *  главный вопрос к накопленной базе, и оно не должно стоить шести тапов.
 *
 *  Границы повторяют серверные (`app/domain/timeframe.py`): календарные сутки,
 *  неделя пн–вс, календарный месяц и год. Разойдись они — одна и та же «неделя»
 *  значила бы на экране и в чате разные дни.
 */
import type { Period, Span } from "./api.ts";
import { isoDay } from "./due.ts";
import { dayLabel, monthName } from "./group.ts";

/** Границы периода, сдвинутого на `back` шагов назад. `back = 0` — текущий. */
export function stepSpan(period: Period, back: number, now: Date = new Date()): Span {
  const day = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  if (period === "day") {
    day.setDate(day.getDate() - back);
    return { from: isoDay(day), to: isoDay(day) };
  }
  if (period === "week") {
    // Понедельник недели: getDay() считает с воскресенья, поэтому +6 % 7.
    day.setDate(day.getDate() - ((day.getDay() + 6) % 7) - back * 7);
    const end = new Date(day);
    end.setDate(end.getDate() + 6);
    return { from: isoDay(day), to: isoDay(end) };
  }
  if (period === "year") {
    const year = day.getFullYear() - back;
    return { from: `${year}-01-01`, to: `${year}-12-31` };
  }
  const first = new Date(day.getFullYear(), day.getMonth() - back, 1);
  // Нулевой день следующего месяца — последний день этого: месяцы разной длины
  // считать руками незачем.
  const last = new Date(first.getFullYear(), first.getMonth() + 1, 0);
  return { from: isoDay(first), to: isoDay(last) };
}

/** Подпись периода между стрелками. Год у месяца показываем только чужой —
 *  «Октябрь 2026» в 2026 году было бы шумом. */
export function stepTitle(period: Period, back: number, now: Date = new Date()): string {
  const span = stepSpan(period, back, now);
  if (period === "day") {
    if (back === 0) return "Сегодня";
    return back === 1 ? "Вчера" : dayLabel(span.from);
  }
  if (period === "week") {
    return back === 0 ? "Эта неделя" : `${dayLabel(span.from)} — ${dayLabel(span.to)}`;
  }
  if (period === "year") return span.from.slice(0, 4);
  const name = monthName(span.from);
  const title = name.charAt(0).toUpperCase() + name.slice(1);
  return span.from.slice(0, 4) === String(now.getFullYear())
    ? title
    : `${title} ${span.from.slice(0, 4)}`;
}
